import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea } from 'recharts';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile } from '../services/profileService';
import { getMemberLabData, getHistory } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import SgotFormula from '../formula/Sgot';
import SgptFormula from '../formula/Sgpt';
import AlpFormula from '../formula/Alp';

const LiverContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#4CAF50'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LiverIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin: 4px 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusLabel = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #4CAF50;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const LiverPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sgotData, setSgotData] = useState([]);
  const [sgptData, setSgptData] = useState([]);
  const [alpData, setAlpData] = useState([]);
  const [bilirubinData, setBilirubinData] = useState([]);
  const [albuminData, setAlbuminData] = useState([]);
  const [proteinData, setProteinData] = useState([]);
  const [ggtData, setGgtData] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [latestValues, setLatestValues] = useState({
    sgot: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    sgpt: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    alp: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    bilirubin: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    albumin: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    protein: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    ggt: { value: '-', status: 'ปกติ', color: '#4CAF50' }
  });
  const [profileData, setProfileData] = useState({ age: 0, gender: '' });
  const [allRecords, setAllRecords] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    sgot: '',
    sgpt: '',
    alp: '',
    bilirubin: '',
    albumin: '',
    protein: '',
    ggt: ''
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      sgot: record.sgot || '',
      sgpt: record.sgpt || '',
      alp: record.alp || '',
      bilirubin: record.bilirubin || '',
      albumin: record.albumin || '',
      protein: record.protein || '',
      ggt: record.ggt || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      console.log('📝 Updating Liver data:', editingRecord.date, editForm);

      const updates = [];

      if (editForm.sgot && editForm.sgot !== editingRecord.sgot) {
        updates.push(updateLabData(editingRecord.date, 'sgot', editForm.sgot));
      }
      if (editForm.sgpt && editForm.sgpt !== editingRecord.sgpt) {
        updates.push(updateLabData(editingRecord.date, 'sgpt', editForm.sgpt));
      }
      if (editForm.alp && editForm.alp !== editingRecord.alp) {
        updates.push(updateLabData(editingRecord.date, 'alp', editForm.alp));
      }
      if (editForm.bilirubin && editForm.bilirubin !== editingRecord.bilirubin) {
        updates.push(updateLabData(editingRecord.date, 'bilirubin', editForm.bilirubin));
      }
      if (editForm.albumin && editForm.albumin !== editingRecord.albumin) {
        updates.push(updateLabData(editingRecord.date, 'albumin', editForm.albumin));
      }
      if (editForm.protein && editForm.protein !== editingRecord.protein) {
        updates.push(updateLabData(editingRecord.date, 'protein', editForm.protein));
      }
      if (editForm.ggt && editForm.ggt !== editingRecord.ggt) {
        updates.push(updateLabData(editingRecord.date, 'ggt', editForm.ggt));
      }

      await Promise.all(updates);

      console.log('✅ Liver data updated successfully');
      alert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      await loadLiverData();
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      console.error('❌ Error updating Liver data:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const [datePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${(parseInt(year) + 543).toString().slice(-2)}`;
  };

  const calculateRisk = (sgot, sgpt, alp, bilirubin, albumin, protein, ggt) => {
    const sgotVal = parseFloat(sgot) || 0;
    const sgptVal = parseFloat(sgpt) || 0;
    const alpVal = parseFloat(alp) || 0;
    const bilirubinVal = parseFloat(bilirubin) || 0;
    const albuminVal = parseFloat(albumin) || 0;
    const proteinVal = parseFloat(protein) || 0;
    const ggtVal = parseFloat(ggt) || 0;

    if (!sgot && !sgpt && !alp && !bilirubin && !albumin && !protein && !ggt) return '-';

    const isSgotAbnormal = sgotVal > 0 && sgotVal > 40;
    const isSgptAbnormal = sgptVal > 0 && sgptVal > 41;
    const isAlpAbnormal = alpVal > 0 && (alpVal < 30 || alpVal > 120);
    const isBilirubinAbnormal = bilirubinVal > 0 && (bilirubinVal < 0.1 || bilirubinVal > 1.2);
    const isAlbuminAbnormal = albuminVal > 0 && (albuminVal < 3.5 || albuminVal > 5.5);
    const isProteinAbnormal = proteinVal > 0 && (proteinVal < 6.0 || proteinVal > 8.3);
    const isGgtAbnormal = ggtVal > 0 && ggtVal > 45;

    if (isSgotAbnormal || isSgptAbnormal || isAlpAbnormal || isBilirubinAbnormal || isAlbuminAbnormal || isProteinAbnormal || isGgtAbnormal) {
      return 'ผิดปกติ';
    }

    return 'ปกติ';
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const datePart = dateString.split(' ')[0];
    const [year, month, day] = datePart.split('-');
    const thaiYear = parseInt(year) + 543;
    const thaiMonth = thaiMonths[parseInt(month) - 1];

    return `${parseInt(day)} ${thaiMonth} ${thaiYear}`;
  };

  const loadLiverData = async () => {
    try {
      setLoading(true);
      console.log('🩺 Liver - Loading data...');

      // ====== ดึงข้อมูลจาก MFU API (เส้นเดียวกับ mfu-admin) ======
      let memberLabData = null;
      let age = 0;
      let gender = 'M';

      try {
        memberLabData = await getMemberLabData();
        console.log('📊 Member Lab Data:', memberLabData);

        if (memberLabData?.profile) {
          age = memberLabData.profile.age || 0;
          gender = memberLabData.profile.gender || 'M';
        }
      } catch (memberLabError) {
        console.warn("⚠️ Could not load member lab data:", memberLabError);
      }

      // Fallback: ถ้าไม่ได้ profile จาก memberLabData
      if (!age) {
        const profileResponse = await getProfile();
        const profile = profileResponse?.data || profileResponse?.profile || {};
        age = profile.age || 0;
        gender = profile.gender || 'M';
      }

      setProfileData({ age, gender });

      const labData = memberLabData?.labData || {};

      // ดึงข้อมูลจาก memberLabData หรือ fallback ไป API เดิม
      let sgotArray = [];
      let sgptArray = [];
      let alpArray = [];
      let bilirubinArray = [];
      let albuminArray = [];
      let proteinArray = [];
      let ggtArray = [];

      // ลองดึงจาก memberLabData ก่อน
      const sgotHistory = getHistory(labData, 'sgot');
      const sgptHistory = getHistory(labData, 'sgpt');
      const alpHistory = getHistory(labData, 'alp');

      if (sgotHistory.length > 0 || sgptHistory.length > 0) {
        sgotArray = sgotHistory.map(item => ({ dateadd: item.date, value: item.value }));
        sgptArray = sgptHistory.map(item => ({ dateadd: item.date, value: item.value }));
        alpArray = alpHistory.map(item => ({ dateadd: item.date, value: item.value }));
        bilirubinArray = getHistory(labData, 'bilirubin').map(item => ({ dateadd: item.date, value: item.value }));
        albuminArray = getHistory(labData, 'albumin').map(item => ({ dateadd: item.date, value: item.value }));
        proteinArray = getHistory(labData, 'protein').map(item => ({ dateadd: item.date, value: item.value }));
        ggtArray = getHistory(labData, 'ggt').map(item => ({ dateadd: item.date, value: item.value }));
      } else {
        // Fallback ไป API เดิม
        const sgotResponse = await getLabDataByName('sgot');
        sgotArray = sgotResponse?.data || [];

        const sgptResponse = await getLabDataByName('sgpt');
        sgptArray = sgptResponse?.data || [];

        const alpResponse = await getLabDataByName('alp');
        alpArray = alpResponse?.data || [];

        const bilirubinResponse = await getLabDataByName('bilirubin');
        bilirubinArray = bilirubinResponse?.data || [];

        const albuminResponse = await getLabDataByName('albumin');
        albuminArray = albuminResponse?.data || [];

        const proteinResponse = await getLabDataByName('protein');
        proteinArray = proteinResponse?.data || [];

        const ggtResponse = await getLabDataByName('ggt');
        ggtArray = ggtResponse?.data || [];
      }

      console.log('📊 Liver Data:', {
        SGOT: sgotArray.length,
        SGPT: sgptArray.length,
        ALP: alpArray.length,
        Bilirubin: bilirubinArray.length,
        Albumin: albuminArray.length,
        Protein: proteinArray.length,
        GGT: ggtArray.length
      });

      const recordsMap = new Map();

      sgotArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, ggt: null });
        }
        recordsMap.get(date).sgot = item.value;
      });

      sgptArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, ggt: null });
        }
        recordsMap.get(date).sgpt = item.value;
      });

      alpArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, ggt: null });
        }
        recordsMap.get(date).alp = item.value;
      });

      bilirubinArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, ggt: null });
        }
        recordsMap.get(date).bilirubin = item.value;
      });

      albuminArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, ggt: null });
        }
        recordsMap.get(date).albumin = item.value;
      });

      proteinArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, ggt: null });
        }
        recordsMap.get(date).protein = item.value;
      });

      ggtArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, ggt: null });
        }
        recordsMap.get(date).ggt = item.value;
      });

      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );

      setAllRecords(combinedRecords);

      const processedSgot = sgotArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      const processedSgpt = sgptArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      const processedAlp = alpArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      const processedBilirubin = bilirubinArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      const processedAlbumin = albuminArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      const processedProtein = proteinArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      const processedGgt = ggtArray.map(item => ({
        date: item.dateadd?.split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      setSgotData(processedSgot);
      setSgptData(processedSgpt);
      setAlpData(processedAlp);
      setBilirubinData(processedBilirubin);
      setAlbuminData(processedAlbumin);
      setProteinData(processedProtein);
      setGgtData(processedGgt);

      let mostRecentDate = '';
      if (sgotArray.length > 0) mostRecentDate = sgotArray[sgotArray.length - 1].dateadd;
      if (sgptArray.length > 0 && sgptArray[sgptArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = sgptArray[sgptArray.length - 1].dateadd;
      }
      if (alpArray.length > 0 && alpArray[alpArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = alpArray[alpArray.length - 1].dateadd;
      }
      if (bilirubinArray.length > 0 && bilirubinArray[bilirubinArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = bilirubinArray[bilirubinArray.length - 1].dateadd;
      }
      if (albuminArray.length > 0 && albuminArray[albuminArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = albuminArray[albuminArray.length - 1].dateadd;
      }
      if (proteinArray.length > 0 && proteinArray[proteinArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = proteinArray[proteinArray.length - 1].dateadd;
      }
      if (ggtArray.length > 0 && ggtArray[ggtArray.length - 1].dateadd > mostRecentDate) {
        mostRecentDate = ggtArray[ggtArray.length - 1].dateadd;
      }

      if (mostRecentDate) {
        setLatestDate(formatThaiDate(mostRecentDate));
      }

      if (sgotArray.length > 0 || sgptArray.length > 0 || alpArray.length > 0 || bilirubinArray.length > 0 || albuminArray.length > 0 || proteinArray.length > 0 || ggtArray.length > 0) {
        const latestSgot = sgotArray[sgotArray.length - 1];
        const latestSgpt = sgptArray[sgptArray.length - 1];
        const latestAlp = alpArray[alpArray.length - 1];
        const latestBilirubin = bilirubinArray[bilirubinArray.length - 1];
        const latestAlbumin = albuminArray[albuminArray.length - 1];
        const latestProtein = proteinArray[proteinArray.length - 1];
        const latestGgt = ggtArray[ggtArray.length - 1];

        // Calculate SGOT status using formula
        const sgotValue = parseFloat(latestSgot?.value || 0);
        const sgotResult = latestSgot ? await SgotFormula(sgotValue, age, gender) : null;
        const colorMap = { 'green': '#4CAF50', 'red': '#ff4757', 'gray': '#e0e0e0' };

        // Calculate SGPT status using formula
        const sgptValue = parseFloat(latestSgpt?.value || 0);
        const sgptResult = latestSgpt ? await SgptFormula(sgptValue, age, gender) : null;

        // Calculate ALP status using formula
        const alpValue = parseFloat(latestAlp?.value || 0);
        const alpResult = latestAlp ? await AlpFormula(alpValue, age, gender) : null;

        let bilirubinStatus = 'ปกติ';
        let bilirubinColor = '#4CAF50';
        let bilirubinValue = parseFloat(latestBilirubin?.value || 0);
        if (latestBilirubin && (bilirubinValue < 0.1 || bilirubinValue > 1.2)) {
          bilirubinStatus = 'ผิดปกติ';
          bilirubinColor = '#FF5252';
        }

        let albuminStatus = 'ปกติ';
        let albuminColor = '#4CAF50';
        let albuminValue = parseFloat(latestAlbumin?.value || 0);
        if (latestAlbumin && (albuminValue < 3.5 || albuminValue > 5.5)) {
          albuminStatus = 'ผิดปกติ';
          albuminColor = '#FF5252';
        }

        let proteinStatus = 'ปกติ';
        let proteinColor = '#4CAF50';
        let proteinValue = parseFloat(latestProtein?.value || 0);
        if (latestProtein && (proteinValue < 6.0 || proteinValue > 8.3)) {
          proteinStatus = 'ผิดปกติ';
          proteinColor = '#FF5252';
        }

        let ggtStatus = 'ปกติ';
        let ggtColor = '#4CAF50';
        let ggtValue = parseFloat(latestGgt?.value || 0);
        if (latestGgt && ggtValue > 45) {
          ggtStatus = 'ผิดปกติ';
          ggtColor = '#FF5252';
        }

        setLatestValues({
          sgot: {
            value: latestSgot ? `${sgotValue.toFixed(0)} U/L` : '-',
            status: sgotResult?.title || 'ปกติ',
            color: sgotResult ? (colorMap[sgotResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: sgotValue
          },
          sgpt: {
            value: latestSgpt ? `${sgptValue.toFixed(0)} U/L` : '-',
            status: sgptResult?.title || 'ปกติ',
            color: sgptResult ? (colorMap[sgptResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: sgptValue
          },
          alp: {
            value: latestAlp ? `${alpValue.toFixed(0)} U/L` : '-',
            status: alpResult?.title || 'ปกติ',
            color: alpResult ? (colorMap[alpResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: alpValue
          },
          bilirubin: {
            value: latestBilirubin ? `${bilirubinValue.toFixed(1)} mg/dL` : '-',
            status: bilirubinStatus,
            color: bilirubinColor
          },
          albumin: {
            value: latestAlbumin ? `${albuminValue.toFixed(1)} g/dL` : '-',
            status: albuminStatus,
            color: albuminColor
          },
          protein: {
            value: latestProtein ? `${proteinValue.toFixed(1)} g/dL` : '-',
            status: proteinStatus,
            color: proteinColor
          },
          ggt: {
            value: latestGgt ? `${ggtValue.toFixed(0)} U/L` : '-',
            status: ggtStatus,
            color: ggtColor
          }
        });
      }

      console.log('✅ Liver - Data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading Liver data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLiverData();
  }, []);

  return (
    <LiverContainer>
      <MainHeader />

      <TopBar>ตับ</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ตับ (Liver Function)</PageTitle>
        {latestDate && <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : (
          <>
            {/* Status Card */}
            {(() => {
              // Check which values are abnormal (not green)
              const isSgotAbnormal = latestValues.sgot.value !== '-' && latestValues.sgot.color !== '#4CAF50';
              const isSgptAbnormal = latestValues.sgpt.value !== '-' && latestValues.sgpt.color !== '#4CAF50';
              const isAlpAbnormal = latestValues.alp.value !== '-' && latestValues.alp.color !== '#4CAF50';

              const bilirubinValue = parseFloat(latestValues?.bilirubin?.value) || 0;
              const albuminValue = parseFloat(latestValues?.albumin?.value) || 0;
              const proteinValue = parseFloat(latestValues?.protein?.value) || 0;
              const ggtValue = parseFloat(latestValues?.ggt?.value) || 0;

              const isBilirubinAbnormal = latestValues?.bilirubin?.value !== '-' && (bilirubinValue < 0.1 || bilirubinValue > 1.2);
              const isAlbuminAbnormal = latestValues?.albumin?.value !== '-' && (albuminValue < 3.5 || albuminValue > 5.5);
              const isProteinAbnormal = latestValues?.protein?.value !== '-' && (proteinValue < 6.0 || proteinValue > 8.3);
              const isGgtAbnormal = latestValues?.ggt?.value !== '-' && ggtValue > 45;

              const hasAbnormal = isSgotAbnormal || isSgptAbnormal || isAlpAbnormal || isBilirubinAbnormal || isAlbuminAbnormal || isProteinAbnormal || isGgtAbnormal;

              // Determine overall card color (use worst color)
              let cardColor = '#4CAF50'; // default green
              if (hasAbnormal) {
                const colors = [];
                if (isSgotAbnormal) colors.push(latestValues.sgot.color);
                if (isSgptAbnormal) colors.push(latestValues.sgpt.color);
                if (isAlpAbnormal) colors.push(latestValues.alp.color);
                if (isBilirubinAbnormal || isAlbuminAbnormal || isProteinAbnormal || isGgtAbnormal) colors.push('#FF5252');

                // Priority: red > orange > yellow > green
                if (colors.includes('#ff4757')) cardColor = '#ff4757';
                else if (colors.includes('#FF9800')) cardColor = '#FF9800';
                else if (colors.includes('#FBBC04')) cardColor = '#FBBC04';
                else if (colors.includes('#FF5252')) cardColor = '#FF5252';
              }

              return (
                <StatusCard $bgColor={cardColor}>
                  <LiverIcon src="./ตับสี.png" alt="ตับ" />
                  <StatusInfo>
                    {!hasAbnormal ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : (
                      <>
                        {isSgotAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>SGOT (AST)</AbnormalName>
                            <AbnormalValue>{latestValues.sgot.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.sgot.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isSgptAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>SGPT (ALT)</AbnormalName>
                            <AbnormalValue>{latestValues.sgpt.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.sgpt.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isAlpAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>ALP</AbnormalName>
                            <AbnormalValue>{latestValues.alp.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.alp.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isBilirubinAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>Total Bilirubin</AbnormalName>
                            <AbnormalValue>{latestValues.bilirubin.value}</AbnormalValue>
                            <AbnormalStatus>ผิดปกติ</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isAlbuminAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>Albumin</AbnormalName>
                            <AbnormalValue>{latestValues.albumin.value}</AbnormalValue>
                            <AbnormalStatus>ผิดปกติ</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isProteinAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>Total Protein</AbnormalName>
                            <AbnormalValue>{latestValues.protein.value}</AbnormalValue>
                            <AbnormalStatus>ผิดปกติ</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isGgtAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>GGT</AbnormalName>
                            <AbnormalValue>{latestValues.ggt.value}</AbnormalValue>
                            <AbnormalStatus>ผิดปกติ</AbnormalStatus>
                          </AbnormalItem>
                        )}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()}

            {/* SGOT Chart */}
            <ChartSection>
              <ChartTitle>SGOT (AST)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (≤ 40)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 40)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={sgotData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={40} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={40} y2={100} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* SGPT Chart */}
            <ChartSection>
              <ChartTitle>SGPT (ALT)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (≤ 41)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 41)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={sgptData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={41} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={41} y2={100} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FFA726"
                    strokeWidth={2}
                    dot={{ fill: '#FFA726', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* ALP Chart */}
            <ChartSection>
              <ChartTitle>Alkaline Phosphatase (ALP)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 30)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (30-120)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 120)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={alpData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 200]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={30} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={30} y2={120} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={120} y2={200} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: '#2196F3', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Total Bilirubin Chart */}
            <ChartSection>
              <ChartTitle>Total Bilirubin (บิลิรูบินรวม)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 0.1)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (0.1-1.2)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 1.2)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={bilirubinData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 2.5]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={0.1} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={0.1} y2={1.2} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={1.2} y2={2.5} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF9800"
                    strokeWidth={2}
                    dot={{ fill: '#FF9800', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Albumin Chart */}
            <ChartSection>
              <ChartTitle>Albumin (อัลบูมิน)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 3.5)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (3.5-5.5)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 5.5)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={albuminData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 7]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={3.5} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={3.5} y2={5.5} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={5.5} y2={7} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#9C27B0"
                    strokeWidth={2}
                    dot={{ fill: '#9C27B0', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Total Protein Chart */}
            <ChartSection>
              <ChartTitle>Total Protein (โปรตีนรวม)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ต่ำ (&lt; 6.0)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (6.0-8.3)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 8.3)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={proteinData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={6.0} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={6.0} y2={8.3} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={8.3} y2={10} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#4CAF50"
                    strokeWidth={2}
                    dot={{ fill: '#4CAF50', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* GGT Chart */}
            <ChartSection>
              <ChartTitle>GGT (Gamma-glutamyl transferase)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (0-45)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (&gt; 45)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={ggtData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <ReferenceArea y1={0} y2={45} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={45} y2={100} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#E91E63"
                    strokeWidth={2}
                    dot={{ fill: '#E91E63', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            <HistorySection>
              <HistoryTitle>ประวัติการตรวจ</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRecords.map((record, index) => {
                      const values = [];
                      if (record.sgot) values.push(`SGOT: ${record.sgot}`);
                      if (record.sgpt) values.push(`SGPT: ${record.sgpt}`);
                      if (record.alp) values.push(`ALP: ${record.alp}`);
                      if (record.bilirubin) values.push(`Bilirubin: ${record.bilirubin}`);
                      if (record.albumin) values.push(`Albumin: ${record.albumin}`);
                      if (record.protein) values.push(`Protein: ${record.protein}`);
                      if (record.ggt) values.push(`GGT: ${record.ggt}`);

                      return (
                        <TableRow key={index}>
                          <TableCell>{formatDisplayDate(record.date)}</TableCell>
                          <TableCell>{values.join(', ') || '-'}</TableCell>
                          <TableCell>{calculateRisk(record.sgot, record.sgpt, record.alp, record.bilirubin, record.albumin, record.protein, record.ggt)}</TableCell>
                          <TableCell>
                            <EditButton onClick={() => handleEditClick(record)}>
                              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </EditButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>SGOT (AST) (U/L)</Label>
              <Input
                type="number"
                step="1"
                value={editForm.sgot}
                onChange={(e) => setEditForm({ ...editForm, sgot: e.target.value })}
                placeholder="≤ 40"
              />
            </FormGroup>

            <FormGroup>
              <Label>SGPT (ALT) (U/L)</Label>
              <Input
                type="number"
                step="1"
                value={editForm.sgpt}
                onChange={(e) => setEditForm({ ...editForm, sgpt: e.target.value })}
                placeholder="≤ 41"
              />
            </FormGroup>

            <FormGroup>
              <Label>Alkaline Phosphatase (U/L)</Label>
              <Input
                type="number"
                step="1"
                value={editForm.alp}
                onChange={(e) => setEditForm({ ...editForm, alp: e.target.value })}
                placeholder="30-120"
              />
            </FormGroup>

            <FormGroup>
              <Label>Total Bilirubin (mg/dL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.bilirubin}
                onChange={(e) => setEditForm({ ...editForm, bilirubin: e.target.value })}
                placeholder="0.1-1.2"
              />
            </FormGroup>

            <FormGroup>
              <Label>Albumin (g/dL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.albumin}
                onChange={(e) => setEditForm({ ...editForm, albumin: e.target.value })}
                placeholder="3.5-5.5"
              />
            </FormGroup>

            <FormGroup>
              <Label>Total Protein (g/dL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.protein}
                onChange={(e) => setEditForm({ ...editForm, protein: e.target.value })}
                placeholder="6.0-8.3"
              />
            </FormGroup>

            <FormGroup>
              <Label>GGT (U/L)</Label>
              <Input
                type="number"
                step="1"
                value={editForm.ggt}
                onChange={(e) => setEditForm({ ...editForm, ggt: e.target.value })}
                placeholder="0-45"
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </LiverContainer>
  );
};

export default LiverPage;
